import React, { useEffect, useState } from "react";
import Tables from "../../../common/components/DataTable/tables";
import { columnConfig } from "../../../operations/pages/OperationHome/columnConfig";
import Pagination from "../../../common/components/Pagination/Pagination";
import "./TransactionPopup.css";
import { useDispatch, useSelector } from "react-redux";
import {
  setSearchFields,
  setNavigatorContext,
} from "../../../redux/slices/operationHomeSlice";
import UIConfig from "../../../common/constants/UIConfig";

function TransactionPopup({ isVisible, onClose, recommendationCardTitle }) {
  const dispatch = useDispatch();
  const [filteredData, setFilteredData] = useState([]);
  const [filteredDataCount, setFilteredDataCount] = useState(0);
  const [fullFilteredData, setFullFilteredData] = useState([]);
  const [dateFilter, setDateFilter] = useState("all"); // "7", "30", "365", or "all"
  const [columnFilters, setColumnFilters] = useState({});

  const searchFields = useSelector(
    (state) => state.operationHome.searchFields
  );

  const PopuptableData = useSelector(
    (state) => state.agentHome.transactionTable
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [indexOfFirstItem, setIndexOfFirstItem] = useState(0);
  const [indexOfLastItem, setIndexOfLastItem] = useState(0);

  /* ---------------- DYNAMIC PAGE SIZE ---------------- */

  const getDynamicPageSize = (length) => {
    if (length <= 10) return 10;
    if (length <= 20) return 20;
    return 50;
  };

  /* ---------------- DATE FILTER FUNCTION ---------------- */

  const filterDataByDate = (data, days) => {
    console.log("[filterDataByDate] Starting. Total raw data:", data?.length, "| Days filter:", days);
    if (days === "all") return data;

    const tz = UIConfig.branding.timeZone || "America/New_York";

    // Get "today" string in target timezone
    const todayStr = new Date().toLocaleDateString("en-US", { timeZone: tz });
    const todayInTZ = new Date(todayStr);

    const cutoffDate = new Date(todayInTZ);
    cutoffDate.setDate(todayInTZ.getDate() - parseInt(days));
    console.log(`[filterDataByDate] 'Today' in EST: ${todayStr} | 'Cutoff Date' for ${days} days ago: ${cutoffDate.toLocaleDateString("en-US", { timeZone: tz })}`);

    const filtered = data.filter((item) => {
      if (!item.engagementDate) {
        console.log(`[filterDataByDate] Excluded item without engagementDate:`, item);
        return false;
      }

      const itemDateStr = new Date(item.engagementDate).toLocaleDateString(
        "en-US",
        { timeZone: tz },
      );
      const itemDateInTZ = new Date(itemDateStr);

      const isValid = itemDateInTZ >= cutoffDate;
      if (!isValid) {
        console.log(`[filterDataByDate] Excluded outdated item: ${itemDateStr} (before cutoff ${cutoffDate.toLocaleDateString()})`);
      }
      return isValid;
    });

    console.log("[filterDataByDate] Finished. Valid items remaining:", filtered.length);
    return filtered;
  };

  /* ---------------- APPLY FILTERS ---------------- */

  // Applies date filter + all active column filters (including quality range)
  const applyAllFilters = (rawData, daysFilter, filters) => {
    let result = filterDataByDate(rawData, daysFilter);

    Object.entries(filters).forEach(([key, val]) => {
      if (!val) return;
      console.log(`[applyAllFilters] Applying Column Filter "${key}" for text "${val}"`);

      if (key === "qualityOfCall" && /^\d+-\d+$/.test(val)) {
        const [min, max] = val.split("-").map(Number);
        result = result.filter((row) => {
          const q = parseFloat(row.qualityOfCall);
          return !isNaN(q) && q >= min && q <= max;
        });
      } else if (key === "engagementDate") {
        const tz = UIConfig.branding.timeZone || "America/New_York";

        // Fix for YYYY-MM-DD coming from the component while our text is MM/DD/YYYY
        let searchDateFormatted = val;
        if (val.includes("-")) {
          const parts = val.split("-");
          if (parts.length === 3) {
            const [y, m, d] = parts;
            searchDateFormatted = `${m}/${d}/${y}`; // Convert to MM/DD/YYYY
          }
        }

        console.log(`[applyAllFilters] Formatted column search string from '${val}' to '${searchDateFormatted}'`);

        result = result.filter((item) => {
          if (!item.engagementDate) return false;
          const formatted = new Date(item.engagementDate).toLocaleDateString(
            "en-US",
            {
              timeZone: tz,
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            },
          );

          const isMatch = formatted === searchDateFormatted || formatted.includes(val);
          if (!isMatch) console.log(`[applyAllFilters] Excluded item with formatted date ${formatted} (did not match column filter ${searchDateFormatted})`);
          return isMatch;
        });
      } else if (key === "FeedbackStatus") {
        // FeedbackStatus may come from the API as:
        //   - boolean true/false  → rendered as "Yes"/"No" in Tbodys
        //   - string "Yes"/"No"   → compare directly
        //   - undefined/null      → treat as "No" (no feedback given)
        result = result.filter((item) => {
          const raw = item[key];
          let displayVal;
          if (raw === true || raw === "Yes" || raw === "yes") {
            displayVal = "yes";
          } else if (raw === false || raw === "No" || raw === "no" || raw === undefined || raw === null) {
            displayVal = "no";
          } else {
            displayVal = String(raw).toLowerCase();
          }
          const isMatch = displayVal.includes(val.toLowerCase());
          console.log(`[applyAllFilters] FeedbackStatus item: raw=${JSON.stringify(raw)} display='${displayVal}' filter='${val}' match=${isMatch}`);
          return isMatch;
        });
      } else {
        result = result.filter((item) => {
          const itemVal = item[key]?.toString()?.toLowerCase() || "";
          const isMatch = itemVal.includes(val.toLowerCase());
          if (!isMatch) console.log(`[applyAllFilters] Excluded item where {${key}: '${itemVal}'} didn't match column filter '${val}'`);
          return isMatch;
        });
      }
    });

    console.log(`[applyAllFilters] Finished column filters. Items remaining: ${result.length}`);
    return result;
  };

  const updateView = (rawData, daysFilter, filters, overrideSize) => {
    const filtered = applyAllFilters(rawData, daysFilter, filters);
    const effectiveSize = overrideSize ?? pageSize;
    const pageRows = filtered.slice(0, effectiveSize);
    setFullFilteredData(filtered);
    setFilteredData(pageRows);
    setFilteredDataCount(filtered.length);
    setCurrentPage(1);
    setIndexOfFirstItem(0);
    setIndexOfLastItem(Math.min(effectiveSize, filtered.length));
    // Navigator uses only the currently visible page rows
    dispatch(setNavigatorContext(pageRows));
  };

  /* ---------------- HANDLE DATE FILTER CHANGE ---------------- */

  const handleDateFilterChange = (days) => {
    setDateFilter(days);
    if (!PopuptableData?.data) return;
    console.log("data all", PopuptableData.data, days, columnFilters);
    updateView(PopuptableData.data, days, columnFilters);
  };

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    if (isVisible && PopuptableData?.data?.length >= 0) {
      const data = PopuptableData?.data ?? [];
      // On initial data load, compute a sensible default page size.
      const initialSize = getDynamicPageSize(data.length);
      setPageSize(initialSize);
      updateView(data, dateFilter, columnFilters, initialSize);
    }

    if (!isVisible) {
      setCurrentPage(1);
      setDateFilter("all");
      setColumnFilters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PopuptableData, isVisible]);

  /* ---------------- PAGINATION ---------------- */

  const paginateHandler = (pageNumber) => {
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, fullFilteredData.length);
    const pageRows = fullFilteredData.slice(startIndex, endIndex);

    setCurrentPage(pageNumber);
    setIndexOfFirstItem(startIndex);
    setIndexOfLastItem(endIndex);
    setFilteredData(pageRows);
    // Keep navigator in sync with the newly visible page
    dispatch(setNavigatorContext(pageRows));
  };

  const pageSizeHandler = (size) => {
    const newSize = Number(size);
    const endIndex = Math.min(newSize, fullFilteredData.length);
    const pageRows = fullFilteredData.slice(0, endIndex);

    setPageSize(newSize);
    setCurrentPage(1);
    setIndexOfFirstItem(0);
    setIndexOfLastItem(endIndex);
    setFilteredData(pageRows);
    // Keep navigator in sync with the new page size
    dispatch(setNavigatorContext(pageRows));
  };

  /* ---------------- SEARCH ---------------- */

  const handleSearch = (columnId, searchTerm) => {
    // Normalise column id to match actual data field names
    let mappedId = columnId;
    if (columnId === "callStatus") mappedId = "status";
    if (columnId === "CallCategory") mappedId = "callCategory";

    const updatedFilters = { ...columnFilters };
    if (!searchTerm || searchTerm.trim() === "") {
      delete updatedFilters[mappedId];
    } else {
      updatedFilters[mappedId] = searchTerm.trim();
    }
    setColumnFilters(updatedFilters);
    updateView(PopuptableData?.data ?? [], dateFilter, updatedFilters);
  };

  const handleClearAllFilters = () => {
    setColumnFilters({});
    updateView(PopuptableData?.data ?? [], dateFilter, {});
  };

  /* ---------------- SORT ---------------- */

  const handleSort = (key, direction) => {
    if (key === "callStatus") key = "status";

    const sortedData = [...fullFilteredData].sort((a, b) => {
      if (direction === "ASC") {
        return a[key] > b[key] ? 1 : -1;
      }
      return a[key] < b[key] ? 1 : -1;
    });

    setFullFilteredData(sortedData);
    setFilteredData(sortedData.slice(0, pageSize));
    setFilteredDataCount(sortedData.length);

    setCurrentPage(1);
    setIndexOfFirstItem(0);
    setIndexOfLastItem(Math.min(pageSize, sortedData.length));
  };

  if (!isVisible) return null;

  /* ---------------- UI ---------------- */

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          <h4>
            {recommendationCardTitle} Improvement Insight : Transaction Level
            Details
          </h4>

          <div className="popup-header-right">
            <div className="date-filter-radio">
              <label className={dateFilter === "all" ? "active" : ""}>
                <input
                  type="radio"
                  name="dateFilter"
                  value="all"
                  checked={dateFilter === "all"}
                  onChange={() => handleDateFilterChange("all")}
                />
                All
              </label>
              <label className={dateFilter === "7" ? "active" : ""}>
                <input
                  type="radio"
                  name="dateFilter"
                  value="7"
                  checked={dateFilter === "7"}
                  onChange={() => handleDateFilterChange("7")}
                />
                7 Days
              </label>
              <label className={dateFilter === "30" ? "active" : ""}>
                <input
                  type="radio"
                  name="dateFilter"
                  value="30"
                  checked={dateFilter === "30"}
                  onChange={() => handleDateFilterChange("30")}
                />
                30 Days
              </label>
              <label className={dateFilter === "365" ? "active" : ""}>
                <input
                  type="radio"
                  name="dateFilter"
                  value="365"
                  checked={dateFilter === "365"}
                  onChange={() => handleDateFilterChange("365")}
                />
                365 Days
              </label>
            </div>
            <button className="close-btn" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>

        <div className="transaction-third-container">
          <Tables
            data={filteredData}
            columnConfig={columnConfig}
            className="popup-table"
            style={{
              width: "143%",
              overflowY: "auto",
              marginTop: "-14px",
            }}
            handleSearchTransactionTable={handleSearch}
            handleClearAllFilters={handleClearAllFilters}
            handleSort={handleSort}
            transactionTable={true}
          />


          <Pagination
            className="TransactionPagination"
            totalPage={
              filteredDataCount > 0
                ? Math.ceil(filteredDataCount / pageSize)
                : 0
            }
            currentPage={currentPage}
            limit={pageSize}
            paginateHandler={paginateHandler}
            setPageSize={pageSizeHandler}
            totalData={filteredDataCount}
            tableStartIndex={indexOfFirstItem + 1}
            tableEndIndex={indexOfLastItem}
            style={{
              marginBottom: "-25px",
              marginTop: "-10px",
              width: "143%",
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default TransactionPopup;